"use client";

import { Link, usePathname } from "@/i18n/navigation";
import { useLocale } from "next-intl";
import styles from "./LocaleSwitcher.module.scss";
import { I18nLocalesType, localesList } from "@/i18n/i18n-config";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "@mui/material";
import { useClickAway } from "@uidotdev/usehooks";

export default function LocaleSwitcher() {
  const locale = useLocale();
  const pathname = usePathname();
  const isTabletOrMobile = useMediaQuery("(max-width: 1024px)");

  const currentCode = localesList[locale as I18nLocalesType];

  const [isOpen, setOpen] = useState(false);

  const btnListRef = useRef<null | HTMLButtonElement>(null);

  const localeListRef = useClickAway<HTMLUListElement>(({ target }) => {
    if (!isTabletOrMobile && !btnListRef.current?.contains(target as HTMLElement) && isOpen) {
      setOpen(false);
    }
  });

  useEffect(() => {
    if (isOpen) {
      localeListRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [isOpen, localeListRef]);

  return (
    <>
      <button
        ref={btnListRef}
        className={`${styles.switcher_btn} ${isOpen && styles.switcher_btn__active}`}
        onClick={() => {
          setOpen(!isOpen);
        }}
      >
        <div className={`${styles.switcher_btn__img}`}>
          <Image
            src={`/images/flags/1x1/${currentCode.toLowerCase()}.svg`}
            alt={currentCode.toLowerCase()}
            width={24}
            height={24}
            quality={100}
          />
        </div>

        <div className={`${styles.switcher_btn__code}`}>{currentCode.toUpperCase()}</div>

        <div className={`${styles.switcher_btn__icon}`}>
          <svg
            viewBox="0 0 17 12"
            fill="currentColor"
            width="18"
            height="100%"
            className={styles.button__icon}
          >
            <use href="/images/sprite.svg#arrow-1-right"></use>
          </svg>
        </div>
      </button>

      {isOpen && (
        <ul ref={localeListRef} className={styles.switcher_list}>
          {Object.entries(localesList).map(([lang, code]) => (
            <li
              className={`${styles.switcher_list__item} ${locale === lang ? styles.switcher_list__item_active : ""}`}
              key={lang}
            >
              <Link
                className={styles.switcher_list__link}
                href={pathname}
                locale={lang as I18nLocalesType}
              >
                <div className={styles.switcher_list__item_image}>
                  <Image
                    src={`/images/flags/1x1/${code.toLowerCase()}.svg`}
                    alt={code.toLowerCase()}
                    width={24}
                    height={24}
                    quality={100}
                  />
                </div>

                <div className={styles.switcher_list__item_code}>{code.toUpperCase()}</div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
